// Place a pallet that is still IN PRODUCTION from the Coldroom map, where the
// loader stands (F05 UF-01). Two doors, one sheet:
//   · a find / scan that answers "awaiting placement" → <PlaceFromProduction/>
//     with the room on screen already picked;
//   · a tap on an EMPTY cell → <PlaceAtCellModal/> asks for the pallet, then
//     opens the same sheet with that cell picked.
// The write is PlacePalletModal's from-production mode → transitionPallet(),
// the one writer the run list and the pallet page also use.
import { useState } from 'react'
import { Layers, Scale } from 'lucide-react'
import { Button } from '@/components/ui'
import Modal from '@/components/Modal'
import PlacePalletModal from '@/modules/packhouse/PlacePalletModal'
import WeighPalletDialog, { type WeighablePallet } from '@/modules/packhouse/WeighPalletDialog'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { READS } from '@/modules/packhouse/ColdroomFind'
import type { RoomTab } from '@/components/DestinationRoomTabs'
import { findInColdroom, hitSentence, type ColdroomHit } from '@/lib/coldroomFind'
import { isExternalReceiptRun } from '@/lib/externalReceiptPallets'
import { useVarietyPlacementMap } from '@/hooks/useCommodities'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'

type Awaiting = Extract<ColdroomHit, { kind: 'awaiting' }>

const LOADING_AREA_NOTE =
  'This variety normally goes to the loading area. Placing it here holds it in the coldroom.'

const WEIGH_COLS = 'pallet_id, run_id, version, product_id, pack_type, grade, size_code, mark, gross_weight_kg, carton_count, tare_overrides'

/** An unweighed pallet: Weigh here opens the same dialog the Pallets card uses, and after the save the pallet is
 *  found again (its version moved) and the sheet carries on to the cell pick. */
function WeighFirst({ hit, onWeighed, onClose }: { hit: Awaiting; onWeighed: (h: Awaiting) => void; onClose: () => void }) {
  const [row, setRow] = useState<WeighablePallet | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const p = hit.pallet

  async function open() {
    setBusy(true); setMsg(null)
    try {
      const { data, error } = await withReadTimeout(
        supabase.from('pallets').select(WEIGH_COLS).eq('pallet_id', p.pallet_id).is('deleted_at', null).maybeSingle(), 'Coldroom: read pallet to weigh')
      if (error || !data) throw error ?? new Error('not found')
      setRow(data as unknown as WeighablePallet)
    } catch {
      setMsg('Could not open the weigh sheet. Check your connection and try again.')
    } finally { setBusy(false) }
  }
  async function after() {
    setRow(null); setBusy(true)
    const r = await findInColdroom(p.pallet_id, READS)
    setBusy(false)
    if (r.kind === 'awaiting') onWeighed(r)
    else setMsg('The weight is saved. Find the pallet again to place it.')
  }

  return (
    <>
      <Modal title={<span className="flex items-center gap-2"><Layers size={16} className="text-warning shrink-0" />{p.pallet_id}</span>}
        onClose={onClose} noFooter initialFocus="card">
        <p className="text-sm text-warning">Weigh this pallet before placing it: it was created from a line without a weight.</p>
        {msg && <p role="status" className="text-caption text-warning mt-2">{msg}</p>}
        <div className="mt-3 flex gap-2">
          <Button className="min-h-[44px]" onClick={() => { void open() }} disabled={busy}><Scale size={16} /> Weigh here</Button>
          <Button variant="ghost" className="min-h-[44px]" onClick={onClose}>Close</Button>
        </div>
      </Modal>
      {row && <WeighPalletDialog pallet={row} onClose={() => setRow(null)} onDone={() => { void after() }} />}
    </>
  )
}

export function PlaceFromProduction({ hit: first, rooms, roomId, cell, onClose }: {
  hit:    Awaiting
  rooms:  RoomTab[]
  roomId: string | null
  cell?:  string | null
  onClose: () => void
}) {
  const tenantId = useActiveTenantId()
  const { data: placementMap } = useVarietyPlacementMap()
  const [hit, setHit] = useState(first)
  const p = hit.pallet
  const external = isExternalReceiptRun(p.run_kind)
  // The write is refused by row security for another business's pallet.
  const mine = !!tenantId && p.tenant_id === tenantId

  if (!mine) {
    return (
      <Modal title={<span className="flex items-center gap-2"><Layers size={16} className="text-warning shrink-0" />{p.pallet_id}</span>}
        onClose={onClose} noFooter initialFocus="card">
        <p className="text-sm text-warning">This pallet belongs to another business, so it cannot be placed here.</p>
        <Button variant="ghost" className="mt-3" onClick={onClose}>Close</Button>
      </Modal>
    )
  }
  if (p.awaiting_weight && !external) return <WeighFirst hit={hit} onWeighed={setHit} onClose={onClose} />
  const goesToLoading = !!p.variety && placementMap?.get(p.variety) === 'LOADING_BAY'
  return (
    <PlacePalletModal
      pallet={{
        pallet_id: p.pallet_id, version: p.version, coldroom_id: null, coldroom_overflow: false,
        status: 'IN_PRODUCTION', awaiting_weight: p.awaiting_weight, skipWeighGate: external,
      }}
      rooms={rooms}
      initialRoomId={roomId}
      initialCell={cell}
      note={goesToLoading ? LOADING_AREA_NOTE : null}
      onClose={onClose}
      onSuccess={onClose}
    />
  )
}

/** An empty cell was tapped: ask which pallet, then hand over to the sheet. */
export function PlaceAtCellModal({ cell, rooms, roomId, onClose }: {
  cell:    string
  rooms:   RoomTab[]
  roomId:  string | null
  onClose: () => void
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [hit, setHit] = useState<Awaiting | null>(null)

  async function look() {
    if (!text.trim()) return
    setBusy(true); setMsg(null)
    const r = await findInColdroom(text, READS)
    setBusy(false)
    if (r.kind === 'awaiting') { setHit(r); return }
    setMsg(r.kind === 'cell' || r.kind === 'overflow'
      ? `${r.label} is already in a coldroom. Use Move on its cell instead.`
      : hitSentence(r, () => 'another room'))
  }

  if (hit) return <PlaceFromProduction hit={hit} rooms={rooms} roomId={roomId} cell={cell} onClose={onClose} />
  return (
    <Modal title={`Place on ${cell}`} onClose={onClose} snapshot={text}
      onSubmit={() => { void look() }} isPending={busy}
      submitLabel="Find pallet" submitPendingLabel="Looking…">
      <label className="block text-caption text-muted mb-1" htmlFor="place-at-cell-pallet">Pallet number — type or scan</label>
      <input id="place-at-cell-pallet" className="field w-full" autoComplete="off" autoFocus required
        value={text} onChange={e => { setText(e.target.value); setMsg(null) }} />
      {msg && <p role="status" className="text-caption text-warning mt-2">{msg}</p>}
    </Modal>
  )
}
